import { useEffect, useRef } from 'react';

type AnalyticsValue = string | number | boolean | null;
type AnalyticsMetadata = Record<string, unknown>;

declare global {
  interface Window {
    umami?: { track: (event: string, data?: Record<string, AnalyticsValue>) => void };
  }
}

type AnalyticsConfig = {
  enabled: boolean;
  hostUrl?: string;
  websiteId?: string;
  scriptPath?: string;
  replayEnabled?: boolean;
};

const pendingUmamiEvents: Array<{ event: string; data: Record<string, AnalyticsValue> }> = [];
const sessionPrefix = 'auri79-proposal-session:';

function scalarMetadata(metadata: AnalyticsMetadata) {
  const safe:Record<string,AnalyticsValue>={};
  for(const [key,value] of Object.entries(metadata)){
    if(typeof value==='string')safe[key]=value.slice(0,160);
    else if(typeof value==='number'&&Number.isFinite(value))safe[key]=value;
    else if(typeof value==='boolean'||value===null)safe[key]=value;
  }
  return safe;
}

export function proposalSessionId(slug: string) {
  const key = `${sessionPrefix}${slug}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

function deliverToUmami(event: string, data: Record<string, AnalyticsValue>) {
  if (window.umami) window.umami.track(event, data);
  else if (pendingUmamiEvents.length < 100) pendingUmamiEvents.push({ event, data });
}

function flushUmamiEvents() {
  if (!window.umami) return;
  pendingUmamiEvents.splice(0).forEach(item => window.umami?.track(item.event, item.data));
}

export function trackProposalEvent(slug: string, eventType: string, metadata: AnalyticsMetadata = {}, beacon = false) {
  const sessionId = proposalSessionId(slug);
  const safe = scalarMetadata(metadata);
  const body = JSON.stringify({ slug, eventType, sessionId, metadata: safe });
  try {
    if (!(beacon && navigator.sendBeacon?.('/api/events', new Blob([body], { type: 'application/json' })))) {
      void fetch('/api/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: beacon }).catch(() => undefined);
    }
    deliverToUmami(eventType, { prospect_slug: slug, ...safe });
  } catch {
    // Analytics must never interrupt a proposal interaction.
  }
}

export function useProposalAnalytics(slug: string | undefined, rendererType: string | undefined) {
  const activeSeconds = useRef(0);
  const milestones = useRef(new Set<number>());

  useEffect(() => {
    if (!slug) return;
    const deviceType = matchMedia('(max-width: 767px)').matches ? 'phone' : matchMedia('(max-width: 1023px)').matches ? 'tablet' : 'desktop';
    const context = {
      deviceType,
      rendererType: rendererType ?? 'unknown',
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrerHost: document.referrer ? new URL(document.referrer).hostname : '',
    };
    trackProposalEvent(slug, 'proposal_view', context);
    trackProposalEvent(slug, 'session_started', context);

    let visible = document.visibilityState === 'visible' && document.hasFocus();
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      if (visible) activeSeconds.current += 1;
      for (const milestone of [10, 30, 60, 120]) {
        if (activeSeconds.current >= milestone && !milestones.current.has(milestone)) {
          milestones.current.add(milestone);
          trackProposalEvent(slug, `engaged_${milestone}s`, { activeSeconds: activeSeconds.current, deviceType });
        }
      }
    }, 1000);
    const heartbeat = window.setInterval(() => {
      trackProposalEvent(slug, 'session_heartbeat', { activeSeconds: activeSeconds.current, elapsedSeconds: Math.round((Date.now() - startedAt) / 1000), deviceType });
    }, 15_000);
    const updateVisibility = () => { visible = document.visibilityState === 'visible' && document.hasFocus(); };
    const finish = () => trackProposalEvent(slug, 'session_ended', { activeSeconds: activeSeconds.current, elapsedSeconds: Math.round((Date.now() - startedAt) / 1000), deviceType }, true);
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('focus', updateVisibility);
    window.addEventListener('blur', updateVisibility);
    window.addEventListener('pagehide', finish);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('focus', updateVisibility);
      window.removeEventListener('blur', updateVisibility);
      window.removeEventListener('pagehide', finish);
      finish();
    };
  }, [rendererType, slug]);

  return { activeSeconds };
}

export function ProposalAnalyticsRuntime() {
  useEffect(() => {
    let disposed = false;
    const scripts: HTMLScriptElement[] = [];
    void fetch('/api/analytics/config', { headers: { accept: 'application/json' } })
      .then(response => response.ok ? response.json() as Promise<AnalyticsConfig> : null)
      .then(config => {
        if (disposed || !config?.enabled || !config.hostUrl || !config.websiteId) return;
        const add = (src: string, recorder = false) => {
          const script = document.createElement('script');
          script.defer = true;
          script.src = `${config.hostUrl}${src}`;
          script.dataset.websiteId = config.websiteId!;
          if (!recorder) {
            script.dataset.performance = 'true';
            script.dataset.excludeSearch = 'true';
            script.dataset.excludeHash = 'true';
            script.dataset.doNotTrack = 'true';
            script.addEventListener('load', flushUmamiEvents, { once: true });
          }
          document.head.appendChild(script);
          scripts.push(script);
        };
        add(config.scriptPath || '/script.js');
        if (config.replayEnabled) add('/recorder.js', true);
      })
      .catch(() => undefined);
    return () => { disposed = true; scripts.forEach(script => script.remove()); };
  }, []);
  return null;
}
